import React, { useState, useEffect, useCallback } from 'react';
import { 
  Container, 
  Row, 
  Col, 
  Card, 
  Button, 
  Table, 
  Modal, 
  Form, 
  Badge, 
  Alert,
  Spinner
} from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import './UserManagement.css';
import axios from 'axios';

const UserManagement = () => {
  const { token, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'success' });
  const [formErrors, setFormErrors] = useState({});
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState(null);
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [formData, setFormData] = useState({
    fullname: '',
    email: '',
    password: '',
    role: '',
    department_college: '',
    account_status: 'Active'
  });

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [filteredUsers, setFilteredUsers] = useState([]);

  const roles = ['Extension Coordinator', 'Extension Head', 'GAD', 'Vice Chancellor for Administration and Finance', 'Vice Chancellor for Research, Development and Extension Services', 'Chancellor', 'Accounting Office', 'Budgeting Office', 'Faculty Extensionist', 'Staff', 'Dean', 'Faculty', 'Admin'];
  const statuses = ['Active', 'Inactive'];

  // Check if user is Admin
  const isAdmin = user?.role === 'Admin';

  // Set up axios default headers and timeout
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      axios.defaults.timeout = 30000; // 30 seconds timeout
    }
  }, [token]);

  // Redirect if not Admin
  useEffect(() => {
    if (user && !isAdmin) {
      window.location.href = '/dashboard';
    }
  }, [user, isAdmin]);

  // Fetch users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/users');
      setUsers(response.data);
      setFilteredUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      setAlert({
        show: true,
        message: 'Failed to load users',
        variant: 'danger'
      });
    } finally {
      setLoading(false);
    }
  };

  // Filter users based on search term and filters
  const filterUsers = useCallback(() => {
    let filtered = users.filter(user => {
      // Search filter (name)
      const matchesSearch = searchTerm === '' || 
        user.fullname.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Role filter
      const matchesRole = roleFilter === '' || user.role === roleFilter;
      
      // Department filter
      const matchesDepartment = departmentFilter === '' || (departmentFilter === 'None' ? !user.department_college : user.department_college === departmentFilter);
      
      // Status filter
      const matchesStatus = statusFilter === '' || user.account_status === statusFilter;
      
      return matchesSearch && matchesRole && matchesDepartment && matchesStatus;
    });
    
    setFilteredUsers(filtered);
  }, [searchTerm, roleFilter, departmentFilter, statusFilter, users]);

  // Effect to filter users when search term or filters change
  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch department enum options
  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await axios.get('/users/department-options');
        setDepartmentOptions(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching department options:', error);
      }
    };
    fetchDepartments();
  }, []);

  // Handle form input changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      fullname: '',
      email: '',
      password: '',
      role: '',
      department_college: '',
      account_status: 'Active'
    });
    setFormErrors({});
    setEditingUser(null);
  };

  // Open modal for adding/editing user
  const handleShowModal = (user = null) => {
    if (user) {
      setEditingUser(user);
      setFormData({
        fullname: user.fullname,
        email: user.email,
        password: '',
        role: user.role,
        department_college: user.department_college,
        account_status: user.account_status
      });
    } else {
      resetForm();
    }
    setFormErrors({});
    setShowModal(true);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (submitting) return; // Prevent multiple submissions
    
    setSubmitting(true);
    setFormErrors({});
    
    // Client-side validation
    const allowedDomain = process.env.REACT_APP_ALLOWED_EMAIL_DOMAIN || 'g.batstate-u.edu.ph';
    
    // Only validate email if it's provided (required for non-Faculty Extensionist, Staff, Dean, and Faculty roles)
    if (formData.email) {
      const email = formData.email.toLowerCase().trim();
      
      if (!email.endsWith(`@${allowedDomain}`)) {
        setFormErrors({ email: 'Invalid email address. Please use your institutional email.' });
        setSubmitting(false);
        return;
      }
      
      if (!email.includes('@')) {
        setFormErrors({ email: 'Please enter a valid email address.' });
        setSubmitting(false);
        return;
      }
      
      if (email.endsWith('@')) {
        setFormErrors({ email: 'Please complete the email address.' });
        setSubmitting(false);
        return;
      }
    }
    
    try {
      if (editingUser) {
        // Update existing user
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't update password if empty
        }
        
        console.log('Updating user:', editingUser.user_id, updateData);
        
        const response = await axios.put(`/users/${editingUser.user_id}`, updateData, {
          timeout: 10000 // 10 second timeout
        });
        
        console.log('Update response:', response);
        
        setAlert({
          show: true,
          message: 'User updated successfully!',
          variant: 'success'
        });
      } else {
        // Create new user
        console.log('Creating user:', formData);
        
        const response = await axios.post('/users', formData, {
          timeout: 10000 // 10 second timeout
        });
        
        console.log('Create response:', response);
        
        setAlert({
          show: true,
          message: 'User created successfully!',
          variant: 'success'
        });
      }
      
      setShowModal(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error saving user - full error object:', error);
      console.error('Error type:', typeof error);
      console.error('Error response:', error?.response);
      console.error('Error message:', error?.message);
      console.error('Error code:', error?.code);
      
      // Handle timeout errors
      if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
        setAlert({
          show: true,
          message: 'Request timed out. Please check your connection and try again.',
          variant: 'danger'
        });
        setSubmitting(false);
        return;
      }
      
      // Handle network errors
      if (!error?.response) {
        setAlert({
          show: true,
          message: 'Network error. Please check your connection and try again.',
          variant: 'danger'
        });
        setSubmitting(false);
        return;
      }
      
      // Handle validation errors with more detail
      if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Map validation errors to form fields
        const errors = {};
        error.response.data.errors.forEach(err => {
          if (err?.path) {
            errors[err.path] = err.msg;
          } else if (err?.param) {
            errors[err.param] = err.msg;
          }
        });
        setFormErrors(errors);
        setSubmitting(false);
        return;
      }
      
      // Handle single error message
      if (error?.response?.data?.error) {
        setAlert({
          show: true,
          message: error.response.data.error,
          variant: 'danger'
        });
        setSubmitting(false);
        return;
      }
      
      // Fallback error message
      setAlert({
        show: true,
        message: 'Failed to save user. Please try again.',
        variant: 'danger'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle activation/deactivation modal show
  const handleStatusToggleShow = (user) => {
    setUserToDeactivate(user);
    setShowDeactivateModal(true);
  };

  // Handle status toggle modal cancel
  const handleStatusToggleCancel = () => {
    setShowDeactivateModal(false);
    setUserToDeactivate(null);
  };

  // Handle user status toggle confirmation
  const handleStatusToggleConfirm = async () => {
    if (!userToDeactivate) return;

    const newStatus = userToDeactivate.account_status === 'Active' ? 'Inactive' : 'Active';
    const action = newStatus === 'Active' ? 'activated' : 'deactivated';

    try {
      await axios.put(`/users/${userToDeactivate.user_id}`, { 
        fullname: userToDeactivate.fullname,
        email: userToDeactivate.email,
        role: userToDeactivate.role,
        department_college: userToDeactivate.department_college,
        account_status: newStatus
      });
      setAlert({
        show: true,
        message: `User ${action} successfully!`,
        variant: 'success'
      });
      setShowDeactivateModal(false);
      setUserToDeactivate(null);
      fetchUsers();
    } catch (error) {
      console.error(`Error ${action} user:`, error);
      setAlert({
        show: true,
        message: error.response?.data?.error || `Failed to ${action} user`,
        variant: 'danger'
      });
    }
  };

  // Close alert
  const handleCloseAlert = () => {
    setAlert({ show: false, message: '', variant: 'success' });
  };

  if (!isAdmin) {
    return (
      <Container className="py-4">
        <Alert variant="danger">
          <Alert.Heading>Access Denied</Alert.Heading>
          <p>You do not have permission to access this page. Only Admin users can manage users.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <div className="container-fluid px-3 px-md-4 py-3">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-3 px-md-4 py-3">
      <div className="row">
        <div className="col-12">
          {/* Page Header */}
          <div className="d-flex justify-content-between align-items-center page-header">
            <div>
              <h3 className="mb-1">User Management</h3>
              <p className="text-muted mb-0">Manage system users and their roles</p>
            </div>
            <Button 
              variant="success" 
              onClick={() => handleShowModal()}
            >
              <i className="bi bi-plus-lg me-2"></i>
              Add New User
            </Button>
          </div>

          <Card className="shadow-sm">
            <Card.Body>
              {alert.show && (
                <Alert variant={alert.variant} dismissible onClose={handleCloseAlert}>
                  {alert.message}
                </Alert>
              )}

              {/* Search and Filter Section */}
              <div className="search-filter-section mb-4">
                <Row className="g-3">
                  {/* Search Input */}
                  <Col md={4}>
                    <div className="search-input-group">
                      <label htmlFor="searchInput" className="form-label small text-muted">
                        Search by Name
                      </label>
                      <div className="input-group">
                        <span className="input-group-text">
                          <i className="bi bi-search"></i>
                        </span>
                        <input
                          type="text"
                          id="searchInput"
                          className="form-control"
                          placeholder="Enter user name..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={() => setSearchTerm('')}
                            title="Clear search"
                          >
                            <i className="bi bi-x"></i>
                          </button>
                        )}
                      </div>
                    </div>
                  </Col>

                  {/* Role Filter */}
                  <Col md={2}>
                    <div className="filter-group">
                      <label htmlFor="roleFilter" className="form-label small text-muted">
                        Role
                      </label>
                      <select
                        id="roleFilter"
                        className="form-select"
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                      >
                        <option value="">All Roles</option>
                        {roles.map(role => (
                          <option key={role} value={role}>{role}</option>
                        ))}
                      </select>
                    </div>
                  </Col>

                  {/* Department Filter */}
                  <Col md={3}>
                    <div className="filter-group">
                      <label htmlFor="departmentFilter" className="form-label small text-muted">
                        Department/College
                      </label>
                      <select
                        id="departmentFilter"
                        className="form-select"
                        value={departmentFilter}
                        onChange={(e) => setDepartmentFilter(e.target.value)}
                      >
                        <option value="">All Departments</option>
                        {departmentOptions.map(dept => (
                          <option key={dept} value={dept}>{dept}</option>
                        ))}
                      </select>
                    </div>
                  </Col>

                  {/* Status Filter */}
                  <Col md={2}>
                    <div className="filter-group">
                      <label htmlFor="statusFilter" className="form-label small text-muted">
                        Status
                      </label>
                      <select
                        id="statusFilter"
                        className="form-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                      >
                        <option value="">All Status</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                  </Col>

                  {/* Clear Filters */}
                  <Col md={1}>
                    <div className="filter-group">
                      <label className="form-label small text-muted">&nbsp;</label>
                      <button
                        className="btn btn-outline-secondary w-100"
                        onClick={() => {
                          setSearchTerm('');
                          setRoleFilter('');
                          setDepartmentFilter('');
                          setStatusFilter('');
                        }}
                        title="Clear all filters"
                      >
                        <i className="bi bi-arrow-clockwise"></i>
                      </button>
                    </div>
                  </Col>
                </Row>

                {/* Results Summary */}
                <div className="mt-3">
                  <small className="text-muted">
                    Showing {filteredUsers.length} of {users.length} users
                  </small>
                </div>
              </div>

              <Table responsive striped hover className="table-full-width">
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Department/College</th>
                    <th>Status</th>
                    <th>Date Registered</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.user_id}>
                      <td>{user.user_id}</td>
                      <td>{user.fullname}</td>
                      <td>{user.email}</td>
                      <td>
                        <Badge bg="info">{user.role}</Badge>
                      </td>
                      <td>{user.department_college || '—'}</td>
                      <td>
                        <Badge 
                          bg={user.account_status === 'Active' ? 'success' : 'danger'}
                          className="status-badge"
                        >
                          {user.account_status}
                        </Badge>
                      </td>
                      <td>
                        {new Date(user.date_registered).toLocaleDateString()}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleShowModal(user)}
                            title="Edit User"
                            className="d-flex align-items-center"
                          >
                            <i className="bi bi-pencil"></i>
                          </Button>
                          <Button
                            variant={user.account_status === 'Active' ? 'outline-warning' : 'outline-success'}
                            size="sm"
                            onClick={() => handleStatusToggleShow(user)}
                            title={user.account_status === 'Active' ? 'Deactivate User' : 'Activate User'}
                            className="d-flex align-items-center"
                          >
                            <i className={user.account_status === 'Active' ? 'bi bi-person-x' : 'bi bi-person-check'}></i>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* No Results Message */}
              {filteredUsers.length === 0 && users.length > 0 && (
                <div className="text-center py-5">
                  <i className="bi bi-search display-1 text-muted"></i>
                  <h5 className="mt-3 text-muted">No users found</h5>
                  <p className="text-muted">
                    Try adjusting your search criteria or filters
                  </p>
                  <Button
                    variant="outline-primary"
                    onClick={() => {
                      setSearchTerm('');
                      setRoleFilter('');
                      setDepartmentFilter('');
                      setStatusFilter('');
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Clear all filters
                  </Button>
                </div>
              )}
            </Card.Body>
          </Card>
        </div>
      </div>

      {/* Add/Edit User Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingUser ? 'Edit User' : 'Add New User'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
                             <Col md={6}>
                 <Form.Group className="mb-3">
                   <Form.Label>Full Name *</Form.Label>
                   <Form.Control
                     type="text"
                     name="fullname"
                     value={formData.fullname}
                     onChange={handleChange}
                     placeholder="Enter full name"
                     required
                     isInvalid={!!formErrors.fullname}
                   />
                   <Form.Control.Feedback type="invalid">
                     {formErrors.fullname}
                   </Form.Control.Feedback>
                 </Form.Group>
               </Col>
              {formData.role !== 'Faculty Extensionist' && formData.role !== 'Staff' && formData.role !== 'Dean' && formData.role !== 'Faculty' && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email Address *</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter email address"
                      required
                      isInvalid={!!formErrors.email}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors.email}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
              )}
            </Row>
            
            <Row>
              {formData.role !== 'Faculty Extensionist' && formData.role !== 'Staff' && formData.role !== 'Dean' && formData.role !== 'Faculty' && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Password {!editingUser && '*'}</Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder={editingUser ? "Leave blank to keep current" : "Enter password"}
                      required={!editingUser}
                      isInvalid={!!formErrors.password}
                    />
                    <Form.Control.Feedback type="invalid">
                      {formErrors.password}
                    </Form.Control.Feedback>
                    {editingUser && (
                      <Form.Text className="text-muted">
                        Leave blank to keep the current password
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              )}
                             <Col md={6}>
                 <Form.Group className="mb-3">
                   <Form.Label>Role *</Form.Label>
                   <Form.Select
                     name="role"
                     value={formData.role}
                     onChange={handleChange}
                     required
                     isInvalid={!!formErrors.role}
                   >
                     <option value="">Select role</option>
                     {roles.map((role) => (
                       <option key={role} value={role}>{role}</option>
                     ))}
                   </Form.Select>
                   <Form.Control.Feedback type="invalid">
                     {formErrors.role}
                   </Form.Control.Feedback>
                 </Form.Group>
               </Col>
            </Row>

            <Row>
                             <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Department/College</Form.Label>
                <Form.Select
                  name="department_college"
                  value={formData.department_college}
                  onChange={handleChange}
                  isInvalid={!!formErrors.department_college}
                >
                  <option value="">Select department/college (optional)</option>
                  {departmentOptions.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {formErrors.department_college}
                </Form.Control.Feedback>
              </Form.Group>
               </Col>
              {formData.role !== 'Faculty Extensionist' && formData.role !== 'Staff' && formData.role !== 'Dean' && formData.role !== 'Faculty' && (
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Account Status</Form.Label>
                    <Form.Select
                      name="account_status"
                      value={formData.account_status}
                      onChange={handleChange}
                    >
                      {statuses.map((status) => (
                        <option key={status} value={status}>{status}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              )}
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  {editingUser ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                editingUser ? 'Update User' : 'Create User'
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Status Toggle Confirmation Modal */}
      <Modal show={showDeactivateModal} onHide={handleStatusToggleCancel} centered>
        <Modal.Header closeButton>
          <Modal.Title className="d-flex align-items-center">
            <i className={`bi bi-exclamation-triangle-fill ${userToDeactivate?.account_status === 'Active' ? 'text-warning' : 'text-success'} me-2`}></i>
            {userToDeactivate?.account_status === 'Active' ? 'Deactivate User' : 'Activate User'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center py-3">
            <i className={`bi ${userToDeactivate?.account_status === 'Active' ? 'bi-person-x-fill text-warning' : 'bi-person-check-fill text-success'}`} style={{ fontSize: '3rem' }}></i>
            <h5 className="mt-3 mb-2">
              Confirm {userToDeactivate?.account_status === 'Active' ? 'Deactivation' : 'Activation'}
            </h5>
            <p className="text-muted mb-3">
              {userToDeactivate?.account_status === 'Active' 
                ? 'Are you sure you want to deactivate this user? This will set their account status to Inactive.'
                : 'Are you sure you want to activate this user? This will set their account status to Active.'
              }
            </p>
            {userToDeactivate && (
              <div className="bg-light p-3 rounded">
                <strong>{userToDeactivate.fullname}</strong>
                <div className="text-muted mt-1">
                  <small>
                    <i className="bi bi-person-fill me-1"></i>
                    Role: {userToDeactivate.role}
                  </small>
                </div>
                <div className="text-muted">
                  <small>
                    <i className="bi bi-envelope me-1"></i>
                    Email: {userToDeactivate.email}
                  </small>
                </div>
                <div className="text-muted">
                  <small>
                    <i className="bi bi-building me-1"></i>
                    Department: {userToDeactivate.department_college || '—'}
                  </small>
                </div>
                <div className="text-muted">
                  <small>
                    <i className="bi bi-tag me-1"></i>
                    Current Status: <span className={userToDeactivate?.account_status === 'Active' ? 'text-success fw-bold' : 'text-danger fw-bold'}>{userToDeactivate?.account_status}</span>
                  </small>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={handleStatusToggleCancel}>
            <i className="bi bi-x-lg me-1"></i>
            Cancel
          </Button>
          <Button 
            variant={userToDeactivate?.account_status === 'Active' ? 'warning' : 'success'} 
            onClick={handleStatusToggleConfirm}
          >
            <i className={`bi ${userToDeactivate?.account_status === 'Active' ? 'bi-person-x-fill' : 'bi-person-check-fill'} me-1`}></i>
            {userToDeactivate?.account_status === 'Active' ? 'Deactivate User' : 'Activate User'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UserManagement;
